import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, Loader2, X } from 'lucide-react';
import {
  fetchNotifications, fetchNotificationsUnreadCount,
  markNotificationRead, markNotificationDismissed,
  type NotificationView,
} from '@/lib/api';
import { OPEN_BELL_EVENT } from '@/lib/todayInbox';
import styles from './NotificationBell.module.css';

/**
 * Notification bell + dropdown panel.
 *
 * Lives in AppShell's top-right nav slot, replacing the prior
 * static placeholder. Two pieces:
 *
 *   1. Bell button with red unread-count badge. Polls
 *      /admin/notifications/unread-count every 30s while
 *      mounted. Shows "99+" when count > 99 to keep the badge
 *      from forcing the bell button taller.
 *
 *   2. Dropdown panel anchored below+right via `position:
 *      absolute; top: 100%+8px; right: 0`. Mirrors the
 *      ThemePicker pattern that ships in the same nav slot —
 *      the wrapper has `position: relative`, the panel sizes
 *      itself to ~380px desktop / full-width-minus-padding on
 *      narrow viewports.
 *
 * Polling cadence: 30s for unread-count (cheap countDocuments
 * on the server, hits the partial index). Full notification
 * list fetched lazily on dropdown open with a 20-row cap; no
 * background polling of the list itself, so an opened-and-
 * left-open dropdown stays fresh-enough without spamming the
 * API.
 *
 * Read/dismiss interactions are optimistic — the local list
 * mutates immediately, the API call fires in the background,
 * and a query invalidation refreshes both list and count.
 */

const POLL_INTERVAL_MS = 30_000;
const LIST_LIMIT = 20;

export interface NotificationBellProps {
  /** Class applied to the bell <button>. AppShell passes its
   *  module-local `styles.iconBtn` so the bell matches the
   *  other top-nav icon buttons (logout, hamburger, etc.). */
  buttonClassName?: string;
}

export function NotificationBell({ buttonClassName }: NotificationBellProps = {}) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Unread count polled every 30s. enabled=true by default so
  // the badge appears even before the dropdown opens. Failing
  // soft (returns 0 on error) so a flaky API call never breaks
  // the navbar.
  const countQ = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => fetchNotificationsUnreadCount().catch(() => 0),
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    staleTime: 5_000,
  });
  const unreadCount = countQ.data ?? 0;

  // List fetched lazily — only when the dropdown opens. Hitting
  // the API here on every navbar mount would be wasteful; the
  // count query covers the badge case without the list payload.
  const listQ = useQuery({
    queryKey: ['notifications', 'list', LIST_LIMIT],
    queryFn: () => fetchNotifications({ limit: LIST_LIMIT }),
    enabled: open,
    staleTime: 10_000,
  });

  // Local optimistic-update layer over the React Query cache.
  // We need this because a dismiss should drop the row
  // immediately even before the network round-trip completes;
  // React Query's invalidation triggers a fresh fetch but with
  // network latency the row stays visible during that gap.
  const [optimisticDismissed, setOptimisticDismissed] = useState<Set<string>>(new Set());
  const [optimisticRead, setOptimisticRead] = useState<Set<string>>(new Set());

  // Reset optimistic state when the list refetches — fresh
  // server data is the new source of truth.
  useEffect(() => {
    if (listQ.dataUpdatedAt > 0) {
      setOptimisticDismissed(new Set());
      setOptimisticRead(new Set());
    }
  }, [listQ.dataUpdatedAt]);

  // Click-outside-to-close. mousedown (not click) so the close
  // fires before any click handlers inside the panel — gives
  // tappable rows a chance to handle their own dismissal cleanly
  // without racing the close.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e: MouseEvent): void => {
      const target = e.target;
      if (!wrapperRef.current || !(target instanceof Node)) return;
      if (!wrapperRef.current.contains(target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [open]);

  // The Today inbox opens the bell for its bell rows (Warren, 21 October 2026, item M).
  useEffect(() => {
    const onOpen = (): void => setOpen(true);
    window.addEventListener(OPEN_BELL_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_BELL_EVENT, onOpen);
  }, []);

  // Esc closes the panel.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const visibleList = useMemo<NotificationView[]>(() => {
    if (!listQ.data) return [];
    return listQ.data.filter((n) => !optimisticDismissed.has(n._id));
  }, [listQ.data, optimisticDismissed]);

  const invalidate = useCallback((): void => {
    void qc.invalidateQueries({ queryKey: ['notifications'] });
  }, [qc]);

  const handleRead = async (n: NotificationView): Promise<void> => {
    if (n.read_at || optimisticRead.has(n._id)) return;
    setOptimisticRead((prev) => new Set(prev).add(n._id));
    try {
      await markNotificationRead(n._id);
    } catch {
      // Roll back on failure so the dot reappears.
      setOptimisticRead((prev) => {
        const next = new Set(prev);
        next.delete(n._id);
        return next;
      });
    }
    invalidate();
  };

  const handleDismiss = async (
    n: NotificationView,
    e: React.MouseEvent,
  ): Promise<void> => {
    e.stopPropagation();
    setOptimisticDismissed((prev) => new Set(prev).add(n._id));
    try {
      await markNotificationDismissed(n._id);
    } catch {
      setOptimisticDismissed((prev) => {
        const next = new Set(prev);
        next.delete(n._id);
        return next;
      });
    }
    invalidate();
  };

  const handleRowClick = async (n: NotificationView): Promise<void> => {
    await handleRead(n);
    if (n.link) {
      setOpen(false);
      // SPA navigation — same shape App.tsx's main router uses.
      window.history.pushState({}, '', n.link);
      window.dispatchEvent(new PopStateEvent('popstate'));
    }
  };

  const handleMarkAllRead = async (): Promise<void> => {
    const unread = visibleList.filter((n) => !n.read_at && !optimisticRead.has(n._id));
    if (unread.length === 0) return;
    setOptimisticRead((prev) => {
      const next = new Set(prev);
      for (const n of unread) next.add(n._id);
      return next;
    });
    // Sequential rather than Promise.all — keeps the server
    // happy under v1 scale (a tenant with 50 unread is the
    // realistic worst case, not 5,000). A future batch endpoint
    // is the right scale-up; flagged in PR notes.
    for (const n of unread) {
      try {
        await markNotificationRead(n._id);
      } catch {
        // Single-row failures don't roll back the optimistic
        // state — invalidate at the end picks up the truth.
      }
    }
    invalidate();
  };

  // Keyboard handler for the row "button". Enter/Space activate
  // the same path as a click; the row is a <button type="button">
  // so the browser already gives us this for free, but we wrap
  // for the async path.
  const onRowKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>,
    n: NotificationView,
  ): void => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      void handleRowClick(n);
    }
  };

  const showLoading = listQ.isLoading && !listQ.data;
  const showError = listQ.isError && !listQ.data;

  const badgeText = unreadCount > 99 ? '99+' : String(unreadCount);

  return (
    <div ref={wrapperRef} className={styles.wrapper}>
      <button
        type="button"
        className={`${buttonClassName ?? ''} ${styles.bellBtn}`.trim()}
        onClick={() => setOpen((v) => !v)}
        aria-label={
          unreadCount > 0
            ? `Notifications, ${unreadCount} unread`
            : 'Notifications'
        }
        aria-expanded={open}
        aria-haspopup="menu"
      >
        <Bell size={18} />
        {unreadCount > 0 && (
          <span className={styles.badge} aria-hidden>
            {badgeText}
          </span>
        )}
      </button>

      {open && (
        <div
          role="menu"
          aria-label="Notifications"
          className={styles.panel}
        >
          <header className={styles.panelHeader}>
            <h2 className={styles.panelTitle}>Notifications</h2>
            <button
              type="button"
              className={styles.markAllBtn}
              onClick={() => void handleMarkAllRead()}
              disabled={visibleList.every((n) => n.read_at || optimisticRead.has(n._id))}
            >
              Mark all read
            </button>
          </header>

          <div className={styles.body}>
            {showError && (
              <div className={styles.errorRow}>
                Couldn&rsquo;t load notifications.
              </div>
            )}

            {showLoading ? (
              <div className={styles.loading}>
                <Loader2 size={14} className={styles.spin} /> Loading…
              </div>
            ) : visibleList.length === 0 ? (
              <div className={styles.empty}>
                <BellOff size={28} className={styles.emptyIcon} />
                <span>You&rsquo;re all caught up.</span>
              </div>
            ) : (
              visibleList.map((n) => {
                const isUnread = !n.read_at && !optimisticRead.has(n._id);
                return (
                  <button
                    key={n._id}
                    type="button"
                    role="menuitem"
                    className={`${styles.row} ${isUnread ? styles.rowUnread : ''}`}
                    onClick={() => void handleRowClick(n)}
                    onKeyDown={(e) => onRowKeyDown(e, n)}
                  >
                    <div className={styles.rowMain}>
                      <span className={styles.rowTitle}>{n.title}</span>
                      <span className={styles.rowBody}>{n.body}</span>
                      <span className={styles.rowMeta}>
                        {formatRelative(occurredAt(n))}
                        {repeatCount(n) > 1 && ` · ×${repeatCount(n)}`}
                      </span>
                    </div>
                    <button
                      type="button"
                      className={styles.dismissBtn}
                      onClick={(e) => void handleDismiss(n, e)}
                      aria-label={`Dismiss "${n.title}"`}
                    >
                      <X size={14} />
                    </button>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Helpers ─────────────────────────────────────────────────────────

/**
 * Coalesced rows (2026-09-16). The server folds repeats of the same
 * alert onto one row and sends `last_occurred_at` (the latest fire) and
 * `occurrence_count` (how many). The admin NotificationView in
 * lib/api.ts has not gained the two fields yet, so they are read off
 * the wider server shape here with the pre-coalesce fallbacks; once
 * api.ts declares them, these two helpers collapse to plain reads.
 */
type CoalescedView = NotificationView & {
  last_occurred_at?: string;
  occurrence_count?: number;
};

function occurredAt(n: NotificationView): string {
  return (n as CoalescedView).last_occurred_at ?? n.created_at;
}

function repeatCount(n: NotificationView): number {
  return (n as CoalescedView).occurrence_count ?? 1;
}

function formatRelative(iso: string): string {
  const then = new Date(iso);
  // An unreadable time shows nothing, never a dash (walkthrough counts, step 029: no dash on the Super Admin's landing).
  if (Number.isNaN(then.getTime())) return '';
  const diffMs = Date.now() - then.getTime();
  if (diffMs < 0) return 'just now';
  const minutes = Math.floor(diffMs / (60 * 1000));
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  if (hours < 48) return 'yesterday';
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return then.toLocaleDateString();
}
